import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  CircleDollarSign,
  Loader2,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Users,
  X,
} from "lucide-react";

import api from "@/lib/axios";

const ANALYSIS_ENDPOINT = (eventId) => `/event/${eventId}/report/analysis`;

const SEVERITY_ORDER = Object.freeze({ high: 0, medium: 1, info: 2 });

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeText(value, fallback = "—") {
  const text = value == null ? "" : String(value).trim();
  return text || fallback;
}

function titleCase(value) {
  return safeText(value, "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeText(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isCancelled(error) {
  return error?.code === "ERR_CANCELED" || error?.name === "CanceledError";
}

function friendlyError(error) {
  const status = error?.response?.status;
  const serverMessage =
    error?.response?.data?.error || error?.response?.data?.message;

  if (status === 400) return serverMessage || "The event ID is invalid.";
  if (status === 401)
    return "Your session could not be verified. Please sign in again.";
  if (status === 403)
    return "You do not have permission to view this event analysis.";
  if (status === 404)
    return "This event or its report data could not be found.";
  if (status >= 500)
    return "The analysis service is temporarily unavailable. Please try again.";
  return (
    serverMessage || error?.message || "The event analysis could not be loaded."
  );
}

function toneForBand(value) {
  const band = String(value || "").toLowerCase();
  if (band === "strong" || band === "good") {
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
  }
  if (band === "risk" || band === "high") {
    return "border-rose-200 bg-rose-50 text-rose-800";
  }
  if (band === "watch" || band === "medium") {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function severityTone(value) {
  const severity = String(value || "").toLowerCase();
  if (severity === "high") return "border-rose-200 bg-rose-50 text-rose-900";
  if (severity === "medium")
    return "border-amber-200 bg-amber-50 text-amber-900";
  return "border-sky-200 bg-sky-50 text-sky-900";
}

function priorityTone(value) {
  const priority = String(value || "").toLowerCase();
  if (priority === "high") return "bg-rose-100 text-rose-800";
  if (priority === "medium") return "bg-amber-100 text-amber-800";
  return "bg-sky-100 text-sky-800";
}

function MetricCard({ icon: Icon, label, value, helper }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {label}
          </div>
          <div className="mt-1 truncate text-xl font-bold text-slate-950">
            {safeText(value)}
          </div>
          {helper ? (
            <div className="mt-1 text-xs text-slate-500">{helper}</div>
          ) : null}
        </div>
        <div className="rounded-xl bg-slate-100 p-2 text-slate-700">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-600">
      {children}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 text-center">
      <div className="rounded-full bg-violet-100 p-4 text-violet-700">
        <Loader2 className="h-7 w-7 animate-spin" aria-hidden="true" />
      </div>
      <div>
        <div className="font-semibold text-slate-950">
          Building event analysis
        </div>
        <div className="mt-1 text-sm text-slate-500">
          Reading the authoritative report data…
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="flex min-h-[360px] flex-col items-center justify-center px-4 text-center">
      <div className="rounded-full bg-rose-100 p-4 text-rose-700">
        <AlertTriangle className="h-7 w-7" aria-hidden="true" />
      </div>
      <div className="mt-4 font-semibold text-slate-950">
        Analysis unavailable
      </div>
      <div className="mt-1 max-w-lg text-sm text-slate-600">{message}</div>
      <button
        type="button"
        onClick={onRetry}
        className="mt-5 inline-flex items-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
      >
        <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
        Try Again
      </button>
    </div>
  );
}

/**
 * Permission note:
 * Render this component only when the page's existing canViewEventReports
 * permission is true. The backend still enforces the same permission itself.
 */
export default function EventAnalysisModal({ open, eventId, onClose }) {
  const titleId = useId();
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const requestSequenceRef = useRef(0);

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const eventIdNumber = Number(eventId);
  const validEventId = Number.isInteger(eventIdNumber) && eventIdNumber > 0;

  const retry = useCallback(() => {
    setReloadKey((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(
      () => closeButtonRef.current?.focus(),
      0,
    );
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const requestSequence = ++requestSequenceRef.current;
    const controller = new AbortController();

    async function loadAnalysis() {
      setAnalysis(null);
      setError("");

      if (!validEventId) {
        setLoading(false);
        setError("A valid event ID is required before analysis can be loaded.");
        return;
      }

      setLoading(true);
      try {
        const { data } = await api.get(ANALYSIS_ENDPOINT(eventIdNumber), {
          signal: controller.signal,
        });

        if (requestSequence !== requestSequenceRef.current) return;
        if (!data?.ok)
          throw new Error("The server returned an invalid analysis response.");
        setAnalysis(data);
      } catch (requestError) {
        if (
          requestSequence !== requestSequenceRef.current ||
          isCancelled(requestError)
        )
          return;
        setError(friendlyError(requestError));
      } finally {
        if (requestSequence === requestSequenceRef.current) setLoading(false);
      }
    }

    loadAnalysis();

    return () => {
      requestSequenceRef.current += 1;
      controller.abort();
    };
  }, [open, eventIdNumber, validEventId, reloadKey]);

  const insights = useMemo(
    () =>
      safeArray(analysis?.smart_insights)
        .slice()
        .sort(
          (left, right) =>
            (SEVERITY_ORDER[String(left?.severity || "info").toLowerCase()] ??
              99) -
            (SEVERITY_ORDER[String(right?.severity || "info").toLowerCase()] ??
              99),
        ),
    [analysis],
  );

  const suggestions = safeArray(analysis?.analysis?.suggestions);
  const riskFlags = safeArray(analysis?.analysis?.risk_flags);
  const readiness = analysis?.analysis?.readiness || {};
  const performance = analysis?.analysis?.performance_summary || {};
  const headline = analysis?.executive_headline || {};
  const marginSignals = analysis?.analysis?.margin_signals || {};
  const staffingSignals = analysis?.analysis?.staffing_signals || {};
  const weakMenuItems = safeArray(marginSignals?.low_margin_menu_items);
  const staffingOverruns = safeArray(staffingSignals?.overruns);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 sm:px-7">
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 rounded-2xl bg-violet-100 p-2.5 text-violet-700">
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-violet-700">
                SOLO Intelligence
              </div>
              <h2
                id={titleId}
                className="truncate text-xl font-bold text-slate-950 sm:text-2xl"
              >
                {analysis?.event_name || `Event #${safeText(eventId, "—")}`}
              </h2>
              <div className="mt-1 text-sm text-slate-500">
                Management analysis
                {analysis?.event_date
                  ? ` · ${formatDate(analysis.event_date)}`
                  : ""}
              </div>
            </div>
          </div>

          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close event analysis"
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-7">
          {loading ? <LoadingState /> : null}
          {!loading && error ? (
            <ErrorState message={error} onRetry={retry} />
          ) : null}

          {!loading && !error && analysis ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <MetricCard
                  icon={CircleDollarSign}
                  label="Client Payable"
                  value={headline.client_total_payable || headline.quote_total}
                  helper="Tax-inclusive client amount"
                />
                <MetricCard
                  icon={TrendingUp}
                  label="Recognised Revenue"
                  value={headline.recognised_revenue}
                  helper="Forecast performance basis"
                />
                <MetricCard
                  icon={BarChart3}
                  label="Gross Margin"
                  value={headline.gross_margin}
                />
                <MetricCard
                  icon={Users}
                  label="Contribution Margin"
                  value={headline.contribution_margin || headline.net_margin}
                  helper="After forecast staffing"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Executive Position
                      </div>
                      <div className="mt-1 text-lg font-bold text-slate-950">
                        {titleCase(
                          performance.performance_band ||
                            headline.performance_band,
                        ) || "Analysis available"}
                      </div>
                    </div>
                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${toneForBand(
                        performance.performance_band ||
                          headline.performance_band,
                      )}`}
                    >
                      {titleCase(
                        performance.performance_band ||
                          headline.performance_band,
                      ) || "Not rated"}
                    </span>
                  </div>

                  {performance?.top_opportunity ? (
                    <div className="mt-4 rounded-2xl border border-violet-200 bg-violet-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">
                        Highest-value opportunity
                      </div>
                      <div className="mt-1 font-semibold text-slate-950">
                        {safeText(performance.top_opportunity.title)}
                      </div>
                      <div className="mt-1 text-sm text-slate-700">
                        {safeText(performance.top_opportunity.detail)}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Readiness
                      </div>
                      <div className="mt-1 text-3xl font-bold text-slate-950">
                        {Number.isFinite(Number(readiness.score))
                          ? `${readiness.score}/100`
                          : safeText(headline.readiness_score)}
                      </div>
                    </div>
                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${toneForBand(readiness.status)}`}
                    >
                      {titleCase(readiness.status) || "Not rated"}
                    </span>
                  </div>
                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className={`h-full rounded-full ${
                        String(readiness.status).toLowerCase() === "strong"
                          ? "bg-emerald-500"
                          : String(readiness.status).toLowerCase() === "risk"
                            ? "bg-rose-500"
                            : "bg-amber-500"
                      }`}
                      style={{
                        width: `${Math.max(0, Math.min(100, Number(readiness.score) || 0))}%`,
                      }}
                    />
                  </div>
                  <div className="mt-3 text-xs text-slate-500">
                    Deterministic score from live event data and configured
                    thresholds.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="font-bold text-slate-950">
                      Priority Insights
                    </h3>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      {insights.length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {insights.length ? (
                      insights.map((item, index) => (
                        <article
                          key={`${item?.code || "insight"}-${index}`}
                          className={`rounded-2xl border p-4 ${severityTone(item?.severity)}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="font-semibold">
                              {safeText(item?.title, titleCase(item?.code))}
                            </div>
                            <span className="rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-bold uppercase">
                              {safeText(item?.severity, "info")}
                            </span>
                          </div>
                          <div className="mt-1 text-sm opacity-90">
                            {safeText(item?.detail)}
                          </div>
                        </article>
                      ))
                    ) : (
                      <EmptyState>
                        No automatic exceptions were returned for this event.
                      </EmptyState>
                    )}
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="font-bold text-slate-950">
                      Recommended Actions
                    </h3>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      {suggestions.length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {suggestions.length ? (
                      suggestions.map((item, index) => (
                        <article
                          key={`${item?.area || "suggestion"}-${index}`}
                          className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="font-semibold text-slate-950">
                              {safeText(item?.title)}
                            </div>
                            <span
                              className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${priorityTone(item?.priority)}`}
                            >
                              {safeText(item?.priority, "low")}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-slate-600">
                            {safeText(item?.detail)}
                          </div>
                          {item?.area ? (
                            <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {titleCase(item.area)}
                            </div>
                          ) : null}
                        </article>
                      ))
                    ) : (
                      <EmptyState>
                        No corrective recommendation currently dominates.
                      </EmptyState>
                    )}
                  </div>
                </section>
              </div>

              {weakMenuItems.length > 0 || staffingOverruns.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {weakMenuItems.length > 0 ? (
                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="border-b border-slate-200 px-5 py-4 font-bold text-slate-950">
                        Lowest Menu Margins
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-5 py-3">Item</th>
                              <th className="px-3 py-3 text-right">Revenue</th>
                              <th className="px-5 py-3 text-right">GP</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {weakMenuItems.map((item, index) => (
                              <tr
                                key={`${item?.menu_item_id || "menu"}-${index}`}
                              >
                                <td className="px-5 py-3 font-medium text-slate-900">
                                  {safeText(item?.name)}
                                </td>
                                <td className="px-3 py-3 text-right text-slate-600">
                                  £{Number(item?.revenue || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-right font-semibold text-slate-900">
                                  {Number(item?.gp_pct || 0).toFixed(2)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ) : null}

                  {staffingOverruns.length > 0 ? (
                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="border-b border-slate-200 px-5 py-4 font-bold text-slate-950">
                        Staffing Overruns
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-5 py-3">Cost Centre</th>
                              <th className="px-3 py-3 text-right">Variance</th>
                              <th className="px-5 py-3 text-right">
                                Variance %
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {staffingOverruns.map((row, index) => (
                              <tr
                                key={`${row?.cost_centre_id || "staffing"}-${index}`}
                              >
                                <td className="px-5 py-3 font-medium text-slate-900">
                                  {safeText(row?.name)}
                                </td>
                                <td className="px-3 py-3 text-right text-rose-700">
                                  £{Number(row?.variance || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-right font-semibold text-rose-700">
                                  {row?.variance_pct == null
                                    ? "—"
                                    : `${Number(row.variance_pct).toFixed(2)}%`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ) : null}
                </div>
              ) : null}

              {riskFlags.length ? (
                <section className="rounded-2xl border border-rose-200 bg-rose-50 p-5">
                  <div className="flex items-center gap-2 font-bold text-rose-900">
                    <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                    Risk Flags
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {riskFlags.map((flag) => (
                      <span
                        key={flag}
                        className="rounded-full border border-rose-200 bg-white px-3 py-1 text-xs font-semibold text-rose-800"
                      >
                        {titleCase(flag)}
                      </span>
                    ))}
                  </div>
                </section>
              ) : (
                <section className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
                  <CheckCircle2
                    className="h-5 w-5 shrink-0"
                    aria-hidden="true"
                  />
                  <div className="text-sm font-medium">
                    No structured risk flags were returned at the current
                    thresholds.
                  </div>
                </section>
              )}

              {analysis?.financial_snapshot?.final_net_profit_calculated !==
              true ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs leading-5 text-slate-600">
                  <strong className="text-slate-800">Financial basis:</strong>{" "}
                  this view reports gross margin and contribution after
                  staffing. It does not label contribution as final net profit
                  because all final operating costs are not yet available.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-5 py-4 sm:px-7">
          <div className="text-xs text-slate-500">
            Live system data · Recommendation support only · Management approval
            remains required
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Close
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
